'use client';
import React, { useState } from 'react';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface FormData {
  email: string;
  password: string;
}

const roles = [
  { id: 'role-super-admin', value: 'super_admin', label: 'Super Admin' },
  { id: 'role-school-mgmt', value: 'school_management', label: 'School Management' },
  { id: 'role-transport-mgr', value: 'transport_manager', label: 'Transport Manager' },
  { id: 'role-workshop-sup', value: 'workshop_supervisor', label: 'Workshop Supervisor' },
  { id: 'role-mechanic', value: 'mechanic', label: 'Mechanic' },
  { id: 'role-electrician', value: 'electrician', label: 'Electrician' },
  { id: 'role-tyre-tech', value: 'tyre_technician', label: 'Tyre Technician' },
  { id: 'role-storekeeper', value: 'store_keeper', label: 'Store Keeper' },
  { id: 'role-fuel-incharge', value: 'fuel_incharge', label: 'Fuel Incharge' },
  { id: 'role-driver', value: 'driver', label: 'Driver' },
  { id: 'role-attendant', value: 'attendant', label: 'Lady Attendant / Route Staff' },
];

const quickAccess = [
  { id: 'qa-admin', role: 'Super Admin', email: 'admin@busworkshop.in', password: 'BW@Admin2026' },
  { id: 'qa-transport', role: 'Transport Mgr', email: 'transport@busworkshop.in', password: 'BW@Trans2026' },
  { id: 'qa-supervisor', role: 'Workshop Sup.', email: 'supervisor@busworkshop.in', password: 'BW@Sup2026' },
  { id: 'qa-mechanic', role: 'Mechanic', email: 'mechanic@busworkshop.in', password: 'BW@Mech2026' },
  { id: 'qa-driver', role: 'Driver', email: 'driver@busworkshop.in', password: 'BW@Driver2026' },
  { id: 'qa-store', role: 'Store Keeper', email: 'stores@busworkshop.in', password: 'BW@Store2026' },
];

const roleRedirectMap: Record<string, string> = {
  super_admin: '/workshop-dashboard',
  school_management: '/workshop-dashboard',
  transport_manager: '/workshop-dashboard',
  workshop_supervisor: '/workshop-dashboard',
  mechanic: '/workshop-task-board',
  electrician: '/workshop-task-board',
  tyre_technician: '/tyre-management',
  store_keeper: '/spare-parts',
  fuel_incharge: '/fuel-management',
  driver: '/driver-complaints',
  attendant: '/driver-complaints',
};

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const router = useRouter();
  const { signIn } = useAuth();

  const handleQuickLogin = (cred: typeof quickAccess[0]) => {
    setEmail(cred.email);
    setPassword(cred.password);
    setLoginError('');
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setLoginError('Please enter your email and password.');
      return;
    }
    setIsLoading(true);
    setLoginError('');
    try {
      const data = await signIn(email, password);
      // Determine redirect based on user role from metadata
      const role = data?.user?.user_metadata?.role || 'driver';
      const redirect = roleRedirectMap[role] || '/workshop-dashboard';
      router.push(redirect);
      router.refresh();
    } catch (err: any) {
      setLoginError(err?.message || 'Invalid credentials. Please check your email and password.');
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-sm">
      {/* Mobile logo */}
      <div className="flex items-center gap-2.5 mb-8 lg:hidden">
        <AppLogo size={32} />
        <div>
          <span className="font-bold text-base text-foreground tracking-tight block leading-tight">LaxmiFleet</span>
          <span className="text-[11px] text-muted-foreground leading-tight block">Workshop Control System</span>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground">Sign in</h2>
        <p className="text-[12px] text-muted-foreground mt-1">
          Enter your credentials to access the workshop system.
        </p>
      </div>

      {loginError && (
        <div className="flex items-start gap-2 p-3 rounded border mb-4 animate-slide-up" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
          <AlertCircle size={13} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] font-medium" style={{ color: '#dc2626' }}>
            {loginError}
          </p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-3.5" noValidate>
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-[12px] font-semibold text-foreground mb-1">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@laxmifleet.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-9 px-3 text-[13px] border rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors border-border"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-[12px] font-semibold text-foreground mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-9 px-3 pr-9 text-[13px] border rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors border-border"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-9 text-white text-[13px] font-semibold rounded hover:opacity-90 active:scale-[0.99] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: '#1d4ed8' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={14} className="animate-spin flex-shrink-0" />
              <span>Signing in...</span>
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-[11px] text-muted-foreground text-center mt-5">
        Access issues?{' '}
        <button type="button" className="text-primary font-medium hover:underline">
          Contact your transport admin
        </button>
      </p>
    </div>
  );
}